import { useEffect, useId, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import type {
  TenantDto,
  TenantImpersonateResponse,
  TenantPoolCapacityDto,
  TenantSubscriptionSyncResponse,
  TenantUpdateRequest,
} from '@shared/api-types';
import { formatDateTime } from '@shared/datetime';
import { adminApiFetch } from '../../lib/adminApiClient';
import { useAdminAuth } from '../../lib/adminAuth';
import { DeleteTenantPanel } from './DeleteTenantPanel';

const TENANT_STATUS_PROVISIONING = 0;
const TENANT_STATUS_ACTIVE = 1;

// While the tenant is Provisioning its database is being created on the Jobs
// host (AAI-129); re-fetch its status on this cadence until it changes.
const PROVISIONING_POLL_MS = 10_000;

const EDITIONS = ['Trial', 'Starter', 'Professional', 'Enterprise'] as const;

export function TenantEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { state: adminState } = useAdminAuth();
  const [row, setRow] = useState<TenantDto | null>(null);
  // The tenant as last loaded/saved — unsaved form edits must not enable Delete.
  const [savedRow, setSavedRow] = useState<TenantDto | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [pollFailed, setPollFailed] = useState(false);
  const statusHintId = useId();
  // The tenant's current elastic pool, read live (AAI-144); null = unknown.
  const [poolName, setPoolName] = useState<string | null>(null);

  const canImpersonate = adminState.status === 'authenticated' && adminState.admin.canImpersonate;
  const provisioning = row?.status === TENANT_STATUS_PROVISIONING;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await adminApiFetch<TenantDto>(`/api/admin/tenants/${id}`);
        if (!cancelled) { setRow(data); setSavedRow(data); }
      } catch {
        if (!cancelled) setToast('Failed to load tenant.');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Look the pool up once the DB name is known, and again when provisioning
  // finishes (the DB only gets a pool at create). Failure just shows "unknown".
  const tenantDatabaseName = row?.tenantDatabaseName;
  useEffect(() => {
    if (!tenantDatabaseName || provisioning) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await adminApiFetch<TenantPoolCapacityDto>('/api/admin/tenant-pools');
        if (!cancelled) setPoolName(data.databasePools[tenantDatabaseName] ?? null);
      } catch {
        if (!cancelled) setPoolName(null);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantDatabaseName, provisioning]);

  // Only the provisioning job moves a tenant out of Provisioning, so poll for it.
  // Merges ONLY the status, so unsaved edits in the form survive. Stops when the
  // status changes, when a poll fails, or on unmount.
  useEffect(() => {
    if (!id || !provisioning || pollFailed) return;
    let cancelled = false;
    let inFlight = false;
    const intervalId = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      void (async () => {
        try {
          const data = await adminApiFetch<TenantDto>(`/api/admin/tenants/${id}`);
          if (!cancelled) setRow((prev) => (prev ? { ...prev, status: data.status } : prev));
        } catch {
          if (!cancelled) {
            setPollFailed(true);
            setToast('Failed to load tenant.');
          }
        } finally {
          inFlight = false;
        }
      })();
    }, PROVISIONING_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [id, provisioning, pollFailed]);

  if (!row) {
    return <div>Loading…</div>;
  }

  // A soft-deleted tenant (AAI-151) is a tombstone: read-only, nothing to edit.
  if (row.deletedUtc) {
    return (
      <div style={{ maxWidth: 640 }}>
        <h2>{row.name}</h2>
        <div style={panelStyle} data-testid="tenant-deleted">
          <div style={{ fontWeight: 600, color: '#b91c1c' }}>
            Deleted · {formatDateTime(row.deletedUtc)}
          </div>
          <div style={{ color: '#64748b', fontSize: 13 }}>
            {row.region.toUpperCase()} · database <code>{row.tenantDatabaseName}</code>
            {row.tenantDatabaseName.startsWith('XXXDELETE-')
              ? ' (kept, not dropped; an operator can drop it to free its pool slot).'
              : ' (rename to XXXDELETE- pending; retried nightly).'}
          </div>
          <div>
            <Button onClick={() => navigate('/tenants')}>Back</Button>
          </div>
        </div>
      </div>
    );
  }

  const hasZohoId = (row.zohoSubscriptionId ?? '').trim().length > 0;

  // PUT the current form state and adopt the server response. Shared by Save and
  // Sync now (the latter persists first so the sync never runs against stale data).
  const persist = async (): Promise<TenantDto> => {
    const body: TenantUpdateRequest = {
      name: row.name,
      includedCreditsPerPeriod: row.includedCreditsPerPeriod,
      overagePeriod: row.overagePeriod,
      mfaRequired: row.mfaRequired,
      status: row.status,
      edition: row.edition,
      // Send "" to detach; the API treats empty as clear, non-empty as set.
      zohoSubscriptionId: (row.zohoSubscriptionId ?? '').trim(),
    };
    const updated = await adminApiFetch<TenantDto>(`/api/admin/tenants/${row.id}`, {
      method: 'PUT',
      body,
    });
    setRow(updated);
    setSavedRow(updated);
    return updated;
  };

  const save = async () => {
    setSaving(true);
    try {
      await persist();
      setToast('Saved.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Start a staff support-login: the API mints a single-use token and returns a
  // redeem URL on the CUSTOMER host (the cookie can only be set there). Open it
  // in a new tab so the admin keeps this console alongside the support session.
  const startSupportLogin = async () => {
    // Open the tab synchronously (still inside the click gesture) so pop-up
    // blockers don't kill it after the async round-trip; navigate it once the
    // token is back. Drop the opener for noopener-equivalent isolation.
    const supportTab = window.open('about:blank', '_blank');
    if (!supportTab) {
      setToast('Allow pop-ups to open a support session.');
      return;
    }
    supportTab.opener = null;
    setImpersonating(true);
    try {
      const resp = await adminApiFetch<TenantImpersonateResponse>(
        `/api/admin/tenants/${row.id}/impersonate`,
        { method: 'POST' },
      );
      supportTab.location.href = resp.redeemUrl;
    } catch (err) {
      supportTab.close();
      setToast(err instanceof Error ? err.message : 'Could not start support session.');
    } finally {
      setImpersonating(false);
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      // Persist pending edits (e.g. a freshly typed subscription id) first, so the
      // server-side sync runs against the value shown in the form, not stale state.
      await persist();
      const resp = await adminApiFetch<TenantSubscriptionSyncResponse>(
        `/api/admin/tenants/${row.id}/subscription/sync`,
        { method: 'POST' },
      );
      // A Zoho sync can change the status (terminal statuses suspend), so the
      // saved copy that gates Delete must follow it too.
      if (resp.tenant) { setRow(resp.tenant); setSavedRow(resp.tenant); }
      setToast(
        resp.outcome === 'skipped'
          ? `Sync skipped: ${resp.message ?? 'no subscription'}`
          : `Subscription ${resp.outcome}.`,
      );
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <h2>Edit tenant</h2>
      <div style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
        {row.region.toUpperCase()} · {row.tenantDatabaseName} · pool {poolName ?? 'unknown'}
      </div>

      <div style={panelStyle}>
        <Field label="Name">
          <input value={row.name} onChange={(e) => setRow({ ...row, name: e.target.value })} style={inputStyle} />
        </Field>
        <Field label="Included credits per period">
          <input
            type="number"
            value={row.includedCreditsPerPeriod}
            onChange={(e) => setRow({ ...row, includedCreditsPerPeriod: Number(e.target.value) })}
            style={inputStyle}
          />
        </Field>
        <Field label="Overage period">
          <select
            value={row.overagePeriod}
            onChange={(e) => setRow({ ...row, overagePeriod: Number(e.target.value) })}
            style={inputStyle}
          >
            <option value={0}>Monthly</option>
            <option value={1}>Annual</option>
          </select>
        </Field>
        <Field label="MFA required">
          <input
            type="checkbox"
            checked={row.mfaRequired}
            onChange={(e) => setRow({ ...row, mfaRequired: e.target.checked })}
          />
        </Field>
        {/* Provisioning is set only by tenant creation and cleared only by the
            provisioning job (the API rejects a manual move either way), so it is
            read-only here and never offered as a choice. */}
        <Field label="Status">
          {provisioning ? (
            <input
              readOnly
              value="Provisioning"
              aria-describedby={statusHintId}
              style={readOnlyStyle}
              data-testid="tenant-status-readonly"
            />
          ) : (
            <select
              value={row.status}
              onChange={(e) => setRow({ ...row, status: Number(e.target.value) })}
              style={inputStyle}
              data-testid="tenant-status-select"
            >
              <option value={1}>Active</option>
              <option value={2}>Suspended</option>
            </select>
          )}
        </Field>
        {provisioning && (
          <div id={statusHintId} style={{ color: '#64748b', fontSize: 12, marginTop: -8 }}>
            Set to Active automatically when the tenant database is ready.
          </div>
        )}

        <Field label="Edition">
          <select
            value={row.edition}
            onChange={(e) => setRow({ ...row, edition: e.target.value })}
            style={inputStyle}
          >
            {EDITIONS.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </Field>
        <div style={{ color: '#64748b', fontSize: 12, marginTop: -8 }}>
          Limits: {row.maxRunners === null ? 'unlimited' : row.maxRunners} runner(s),{' '}
          {row.maxInstancesPerRunner} instance(s)/runner, {row.includedCreditsPerPeriod.toLocaleString()} credits.
          A Zoho sync overwrites the edition.
        </div>

        <Field label="Zoho subscription ID">
          <input
            value={row.zohoSubscriptionId ?? ''}
            placeholder="(none)"
            onChange={(e) => setRow({ ...row, zohoSubscriptionId: e.target.value })}
            style={inputStyle}
          />
        </Field>
        <div style={{ color: '#64748b', fontSize: 12, marginTop: -8 }}>
          Subscription status: {row.subscriptionStatus ?? '—'} · Last synced:{' '}
          {formatDateTime(row.subscriptionSyncedUtc, 'never')}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button onClick={() => navigate('/tenants')}>Back</Button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              disabled={syncing || saving || !hasZohoId}
              title={hasZohoId ? 'Save changes, then pull the subscription from Zoho' : 'Set a Zoho subscription id first'}
              onClick={() => void syncNow()}
            >
              {syncing ? 'Syncing…' : 'Sync now'}
            </Button>
            <Button themeColor="primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      {canImpersonate && (
        <div style={{ ...panelStyle, marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Support login</div>
          <div style={{ color: '#64748b', fontSize: 13 }}>
            Open this tenant in the customer app as <strong>CloudRadial Support</strong> (full admin)
            to investigate on the customer&rsquo;s behalf. The session is audited and credential
            changes are blocked. Available for active tenants only.
          </div>
          <div>
            <Button
              disabled={impersonating || row.status !== TENANT_STATUS_ACTIVE}
              title={
                row.status === TENANT_STATUS_ACTIVE
                  ? 'Open a support session in a new tab'
                  : 'Support login is available for active tenants only'
              }
              onClick={() => void startSupportLogin()}
            >
              {impersonating ? 'Starting…' : 'Log in as support'}
            </Button>
          </div>
        </div>
      )}

      {/* Delete acts on the SAVED status: suspend + Save first, as in ServiceAI. */}
      <DeleteTenantPanel tenant={savedRow ?? row} onDeleted={(t) => { setRow(t); setSavedRow(t); }} />

      <NotificationGroup style={{ position: 'fixed', right: 16, bottom: 16 }}>
        {toast && (
          <Notification type={{ style: 'info', icon: true }} closable onClose={() => setToast(null)}>
            <span>{toast}</span>
          </Notification>
        )}
      </NotificationGroup>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{label}</span>
      {children}
    </label>
  );
}

const panelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  padding: 24,
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
};

const inputStyle: React.CSSProperties = {
  padding: 10,
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: 14,
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: '#f8fafc',
  color: '#475569',
};
